import React, { useEffect, useMemo } from 'react';
import { Paper, Box, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';

const ProjectMetricsSummary = () => {
  const { formData, budgetSummaryRows, setMetricsSummaryRows } = useEstimator();

  const rowData = useMemo(() => {
    const getTotalPrice = (no) => {
      const row = budgetSummaryRows?.find((r) => r.no === no);
      if (!row || typeof row.totalPrice !== 'string') return 0;
      return parseFloat(row.totalPrice.replace(/[^0-9.-]+/g, '')) || 0;
    };

    const miles = parseFloat(formData?.quantities?.[6]?.quantity) || 0;
    const diameter = parseFloat(formData?.quantities?.[0]?.quantity) || 0;
    const taxCostTotal = parseFloat(formData?.costCategory?.find((item) => item.rowNumber === 29)?.taxCost) || 0;
    const total = getTotalPrice(28);
    const contigencyTotal = getTotalPrice(27);
    const contigencyPercent =
      parseFloat(formData.costAssumptions?.find((assumption) => assumption.type === 'Contingency')?.value) || 0;

    let perInchMile = null;
    if (diameter && miles) {
      const value = total / diameter / miles;
      perInchMile = value;
    }

    return [
      { no: 1, metric: 'Project Miles', value: miles || '—' },
      { no: 2, metric: 'Project Diameter', value: diameter || '—' },
      { no: 3, metric: '$ /in-mile', value: perInchMile },
      { no: 4, metric: 'Contingency %', value: contigencyPercent },
      { no: 5, metric: 'Total Contingency', value: contigencyTotal },
      { no: 6, metric: 'Total Tax', value: taxCostTotal },
    ];
  }, [formData, budgetSummaryRows]);

  useEffect(() => {
    setMetricsSummaryRows(rowData);
  }, [rowData, setMetricsSummaryRows]);

  const columnDefs = [
    {
      headerName: 'Metric',
      field: 'metric',
      flex: 1,
      cellStyle: { fontWeight: '500', fontSize: '14px' },
    },
    {
      headerName: 'Value',
      field: 'value',
      flex: 1,
      cellStyle: { textAlign: 'left', fontSize: '14px' },
      valueFormatter: (params) => {
        const { value, data } = params;
        if (value == null || value === '—') return '—';

        if (typeof value === 'number') {
          if (data.metric === '$ /in-mile' || data.metric === 'Total Contingency' || data.metric === 'Total Tax') {
            return `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          }
          if (data.metric === 'Contingency %') {
            return `${value}%`;
          }
          return value.toLocaleString();
        }

        return value;
      },
    },
  ];

  return (
    <Paper
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        p: 0,
        backgroundColor: '#223A64',
        border: '1px solid #e0e0e0',
      }}
    >
      {/* Title */}
      <Box
        sx={{
          backgroundColor: '#223A64',
          py: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>
          Project Metrics
        </Typography>
      </Box>

      {/* Table */}
      <Box
        className="ag-theme-alpine"
        sx={{
          width: '100%',
          '& .ag-root-wrapper': { border: 'none' },
          '& .ag-header': { display: 'none' },
          '& .ag-row': { borderBottom: '1px solid #eee' },
          '& .ag-cell': { display: 'flex', alignItems: 'center' },
        }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          headerHeight={0}
          domLayout="autoHeight"
          suppressMenuHide
          suppressCellFocus
          suppressMovableColumns
          suppressColumnVirtualisation
          defaultColDef={{
            resizable: true,
            sortable: false,
            filter: false,
          }}
        />
      </Box>
    </Paper>
  );
};

export default ProjectMetricsSummary;
